/**
 * PDF export (spec § 8.2) — jsPDF A4, navy header band, footer with page i/n.
 *
 * KNOWN LIMITATION, inherited from the prototype and flagged in spec § 11:
 * jsPDF's built-in fonts are Helvetica/Times/Courier, all Latin-only, so
 * Chinese characters render as boxes. Embedding Noto Sans SC would fix it but
 * costs several megabytes in the bundle — unacceptable for an app whose whole
 * point is cold-booting on a bad connection.
 *
 * So the app offers the better path instead: the report VIEWER renders HTML,
 * where the browser draws Chinese correctly, and Print → "Save as PDF"
 * produces a complete PDF with CJK intact. `hasCJK` lets the UI say so.
 *
 * jspdf-autotable here is 5.0.7 (the repo's version), whose API is the
 * functional `autoTable(doc, options)` — not the prototype's 3.x plugin method.
 */

import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { CJK_FONT, loadCjkFont, registerCjkFont } from './cjkFont';
import {
  bestQuoted,
  contactLabel,
  dateLabel,
  landed,
  money,
  paidAtBooth,
  priceLine,
  primaryContact,
  riskFlags,
  supplierDecision,
  supplierRating,
  supplierSort,
} from '../rules';

const NAVY = [14, 27, 51];
const GOLD = [233, 179, 60];
const INK = [20, 33, 61];
const MUTED = [107, 115, 133];

const MARGIN = 14;

/** Does any text in this record need a CJK font? */
export function hasCJK(supplier) {
  if (!supplier) return false;
  const re = /[㐀-䶿一-鿿　-〿＀-￯]/;
  const fields = [
    supplier.company,
    supplier.chinaNotes,
    ...(supplier.products || []).flatMap((p) => [p.name, p.notes]),
    ...(supplier.contacts || []).flatMap((c) => [c.role, c.other]),
  ];
  return fields.some((f) => f && re.test(String(f)));
}

/* ── CJK text ───────────────────────────────────────────────────────────── */

const CJK_RE = /[㐀-䶿一-鿿　-〿＀-￯]/;
export const isCJK = (t) => CJK_RE.test(String(t == null ? '' : t));

/**
 * The handful of characters above U+00FF that Helvetica CAN encode, because
 * WinAnsi maps them into its upper range.
 */
const WINANSI_EXTRAS = new Set(
  [
    0x20ac, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021, 0x02c6, 0x2030, 0x0160, 0x2039,
    0x0152, 0x017d, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022, 0x2013, 0x2014, 0x02dc, 0x2122,
    0x0161, 0x203a, 0x0153, 0x017e, 0x0178,
  ].map((c) => String.fromCharCode(c)),
);

/**
 * Symbols Helvetica cannot encode but that have a perfectly good ASCII
 * equivalent.
 *
 * Rasterising is the fallback for text with no alternative — CJK. Using it
 * for a single "≈" meant the whole string "CNY 100.00 ≈ $14.00" became an
 * image, which then had to shrink to fit its column and ended up noticeably
 * smaller than the "$5.90" next to it, and unselectable. Folding the symbol
 * keeps the line as real text at full size.
 */
const SYMBOL_FALLBACKS = {
  '≈': '~',
  '→': '->',
  '←': '<-',
  '★': '*',
  '☆': '*',
  '✓': 'Y',
  '✗': 'N',
  '≤': '<=',
  '≥': '>=',
  '−': '-',
};

export function foldSymbols(t) {
  return String(t == null ? '' : t).replace(/[≈→←★☆✓✗≤≥−]/g, (c) => SYMBOL_FALLBACKS[c] || c);
}

/**
 * Can jsPDF's built-in font draw this string, after folding?
 *
 * jsPDF encodes with WinAnsi, so any character outside Latin-1 plus the short
 * list above is silently mangled. Whatever foldSymbols() could not replace
 * with an ASCII equivalent — in practice CJK — has to be drawn as an image.
 */
export function needsRaster(t) {
  const s = String(t == null ? '' : t);
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (ch.charCodeAt(0) > 0xff && !WINANSI_EXTRAS.has(ch)) return true;
  }
  return false;
}

/**
 * Draw text that jsPDF's Latin-only fonts cannot encode.
 *
 * Helvetica has no CJK glyphs, so a Chinese supplier name came out as mojibake
 * ("mñW3y»~¿mK‹ÖS"), which is worse than omitting it — it looks like data
 * corruption. Embedding Noto Sans SC would fix it in vector form and costs
 * several megabytes in a bundle that has to cold-boot on a bad connection.
 *
 * So only the strings that actually contain CJK are rasterised, by the browser
 * that already has the fonts, and placed as small images. Everything else stays
 * real vector text — searchable, selectable and tiny. A supplier list has a
 * handful of such strings, so the cost is negligible and nothing freezes.
 */
function cjkText(doc, text, x, y, opts = {}) {
  const { size = 10, bold = false, color = INK, align = 'left', maxWidth } = opts;
  const str = String(text);

  // 3x is the point where the glyphs stop looking soft in print; 4x
  // tripled the file size for no visible gain, and these reports get shared
  // over hotel wifi.
  const SCALE = 3;
  const px = Math.max(8, size) * SCALE;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const font = `${bold ? '700 ' : ''}${px}px "PingFang SC","Microsoft YaHei","Noto Sans SC","Hiragino Sans GB",sans-serif`;
  ctx.font = font;

  const metrics = ctx.measureText(str);
  canvas.width = Math.ceil(metrics.width) + 4;
  canvas.height = Math.ceil(px * 1.35);

  const c2 = canvas.getContext('2d');
  c2.font = font;
  c2.textBaseline = 'alphabetic';
  c2.fillStyle = `rgb(${color[0]},${color[1]},${color[2]})`;
  c2.fillText(str, 2, px);

  // jsPDF font sizes are points; 1pt = 0.3528mm.
  let hMm = (px * 0.3528 * 1.35) / SCALE;
  let wMm = (canvas.width / canvas.height) * hMm;

  if (maxWidth && wMm > maxWidth) {
    const k = maxWidth / wMm;
    wMm = maxWidth;
    hMm *= k;
  }

  const left = align === 'right' ? x - wMm : align === 'center' ? x - wMm / 2 : x;
  // doc.text() places the BASELINE at y; an image is placed from its top.
  const top = y - hMm * 0.78;
  doc.addImage(canvas.toDataURL('image/png'), 'PNG', left, top, wMm, hMm);
  return wMm;
}

/** doc.text(), but routed through the rasteriser when the string has CJK. */
function text(doc, str, x, y, opts = {}) {
  const s = foldSymbols(str);
  if (!s) return;

  if (!needsRaster(s)) {
    doc.text(s, x, y, opts.align ? { align: opts.align } : undefined);
    return;
  }

  // Real glyphs when the font loaded: selectable, searchable, crisp. The
  // rasteriser stays as the fallback for a first run with no network.
  if (docHasCjkFont) {
    const prev = doc.getFont();
    doc.setFont(CJK_FONT, opts.bold ? 'bold' : 'normal');
    if (opts.size) doc.setFontSize(opts.size);
    if (opts.color) doc.setTextColor(...opts.color);
    doc.text(s, x, y, opts.align ? { align: opts.align } : undefined);
    doc.setFont(prev.fontName, prev.fontStyle);
    return;
  }

  cjkText(doc, s, x, y, opts);
}

/**
 * autoTable hooks that give table cells the same CJK treatment.
 *
 * autoTable draws its own text, so the wrapper above never sees it. These two
 * hooks blank a CJK cell's text before it is drawn and paint the rasterised
 * version in its place afterwards, inside the cell box.
 */
function cjkCellHooks(doc) {
  const pending = new Map();
  return {
    didParseCell: (data) => {
      const original = Array.isArray(data.cell.text)
        ? data.cell.text.join(' ')
        : String(data.cell.text || '');
      // Fold first: a price line is only unprintable because of its "≈", and
      // folding keeps it as full-size selectable text instead of a shrunken
      // image in a narrow column.
      const raw = foldSymbols(original);
      if (raw !== original) data.cell.text = [raw];
      if (!needsRaster(raw)) return;
      // With the font registered autoTable can draw the cell itself; just
      // point the cell at the CJK face and leave the text in place.
      if (docHasCjkFont) {
        data.cell.styles.font = CJK_FONT;
        data.cell.styles.fontStyle = data.section === 'head' ? 'bold' : 'normal';
        return;
      }
      pending.set(`${data.row.index}:${data.column.index}:${data.section}`, raw);
      data.cell.text = [''];
    },
    didDrawCell: (data) => {
      const key = `${data.row.index}:${data.column.index}:${data.section}`;
      const raw = pending.get(key);
      if (!raw) return;
      const pad = 2;
      const size = 8;
      const color = data.section === 'head' ? [255, 255, 255] : INK;
      // Top-aligned, like autoTable's own cells — bottom-aligning dropped the
      // Chinese below the Latin baseline of its neighbours in the same row.
      cjkText(doc, raw, data.cell.x + pad, data.cell.y + pad + size * 0.3528 * 0.95, {
        size,
        bold: data.section === 'head',
        color,
        maxWidth: data.cell.width - pad * 2,
      });
    },
  };
}

/* ── Chrome ─────────────────────────────────────────────────────────────── */

/** True when the current document has the CJK face registered. */
let docHasCjkFont = false;

function newDoc() {
  // Letter, matching every other BeeNexa report (see loanReport's downloadPdf).
  // Every measurement below reads pageSize.getWidth()/getHeight(), so the
  // layout follows the page rather than assuming A4.
  const doc = new jsPDF({ unit: 'mm', format: 'letter' });
  docHasCjkFont = registerCjkFont(doc);
  doc.setFont('helvetica', 'normal');
  return doc;
}

function header(doc, title, subtitle) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, w, 24, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  text(doc, String(title || '').slice(0, 60), MARGIN, 11, { size: 14, bold: true, color: [255, 255, 255], maxWidth: w - MARGIN * 2 - 45 });

  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(195, 204, 222);
    text(doc, String(subtitle).slice(0, 110), MARGIN, 17.5, { size: 9, color: [195, 204, 222], maxWidth: w - MARGIN * 2 - 45 });
  }

  doc.setTextColor(...GOLD);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('Fair Tracker · BeeNexa', w - MARGIN, 11, { align: 'right' });

  doc.setTextColor(...INK);
  return 32; // first usable y
}

/** Footer on every page: date on the left, page i/n on the right. */
function footers(doc) {
  const pages = doc.internal.getNumberOfPages();
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  const stamp = new Date().toLocaleDateString();

  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(stamp, MARGIN, h - 8);
    doc.text(`page ${i}/${pages}`, w - MARGIN, h - 8, { align: 'right' });
  }
}

/** Page-break helper: returns a y that is guaranteed to have `need` mm below it. */
function space(doc, y, need = 20) {
  const h = doc.internal.pageSize.getHeight();
  if (y + need > h - 18) {
    doc.addPage();
    return MARGIN + 4;
  }
  return y;
}

function heading(doc, y, label) {
  const next = space(doc, y, 14);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  doc.text(String(label), MARGIN, next);
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.6);
  doc.line(MARGIN, next + 1.5, MARGIN + 18, next + 1.5);
  return next + 7;
}

/**
 * A supplier's in-flow title bar, for reports where suppliers share pages.
 *
 * `header()` paints the full-width navy page banner, which only reads right at
 * the very top of a page. This is its compact sibling: a navy strip with the
 * company and its decision, so a reader still sees where one supplier ends and
 * the next begins when three of them sit on one sheet.
 */
function supplierHeading(doc, y, supplier, fair) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...NAVY);
  doc.rect(MARGIN, y, w - MARGIN * 2, 9, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(255, 255, 255);
  text(doc, supplier.company || 'Supplier', MARGIN + 3, y + 6.2, { size: 10.5, bold: true, color: [255, 255, 255], maxWidth: w - MARGIN * 2 - 60 });

  const right = supplierDecision(supplier) || '';
  if (right) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...GOLD);
    text(doc, right, w - MARGIN - 3, y + 6.2, { size: 8.5, color: GOLD, align: 'right' });
  }

  doc.setTextColor(...INK);
  return y + 14;
}

/**
 * The supplier's key facts, in TWO columns.
 *
 * These used to print as one stacked column: eleven labelled lines, each
 * ~5mm tall, most of the row empty to the right. Eleven facts became half a
 * page of white space per supplier, and it looked nothing like the viewer,
 * which lays the same facts out as a compact tile grid. Two columns halve the
 * height and read closer to the screen.
 *
 * A value too long for its column (a contact line, typically) takes the full
 * width on its own row rather than colliding with its neighbour — the overlap
 * that showed up once names got long.
 */
function keyValues(doc, y, rows) {
  const pageW = doc.internal.pageSize.getWidth();
  const usable = pageW - MARGIN * 2;
  const colW = usable / 2;
  const labelW = 26;
  const valueW = colW - labelW - 4;

  // Callers pass null for a row that does not apply in their context, so drop
  // those before destructuring.
  const present = rows.filter(Boolean).filter(([, v]) => v != null && String(v).trim() !== '');

  doc.setFontSize(9);
  let cursor = y;
  let col = 0;
  let rowTop = cursor;

  const draw = (k, v, x, top) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...MUTED);
    doc.text(String(k), x, top);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...INK);
    const folded = foldSymbols(v);
    if (needsRaster(folded)) {
      if (docHasCjkFont) {
        text(doc, folded, x + labelW, top, { size: 9 });
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        return 4.6;
      }
      cjkText(doc, folded, x + labelW, top, { size: 9, maxWidth: valueW });
      return 4.6;
    }
    const lines = doc.splitTextToSize(folded, valueW);
    doc.text(lines, x + labelW, top);
    return Math.max(4.6, lines.length * 4.4);
  };

  for (const [k, v] of present) {
    const value = foldSymbols(v);
    // Wide values get their own full-width row; everything else pairs up.
    const wide = doc.getTextWidth(value) > valueW;

    if (wide) {
      if (col === 1) {
        cursor = rowTop + 5;
        col = 0;
      }
      cursor = space(doc, cursor, 6);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...MUTED);
      doc.text(String(k), MARGIN, cursor);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...INK);
      if (needsRaster(value)) {
        if (docHasCjkFont) {
          text(doc, value, MARGIN + labelW, cursor, { size: 9 });
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
        } else {
          cjkText(doc, value, MARGIN + labelW, cursor, { size: 9, maxWidth: usable - labelW });
        }
        cursor += 4.8;
        continue;
      }
      const lines = doc.splitTextToSize(value, usable - labelW);
      doc.text(lines, MARGIN + labelW, cursor);
      cursor += Math.max(4.6, lines.length * 4.4);
      continue;
    }

    if (col === 0) {
      cursor = space(doc, cursor, 6);
      rowTop = cursor;
      draw(k, value, MARGIN, rowTop);
      col = 1;
    } else {
      draw(k, value, MARGIN + colW, rowTop);
      cursor = rowTop + 4.8;
      col = 0;
    }
  }

  if (col === 1) cursor = rowTop + 4.8;
  return cursor + 2;
}

/* ── One supplier's body, reused by all three reports ──────────────────── */

/**
 * One supplier's facts.
 *
 * `inFlow` means the supplier sits under a supplierHeading() bar inside a
 * multi-supplier report, where the fair name is already in the page banner.
 * Repeating it per supplier — as this did — printed "Fair / Canton Fair 2026"
 * seven times in one report and did not match the viewer, which shows it once.
 */
function supplierBody(doc, y, supplier, fair, { inFlow = false } = {}) {
  const l = landed(supplier);
  const best = bestQuoted(supplier, fair);
  const contact = primaryContact(supplier);
  const products = supplier.products || [];

  /* SAME FACTS, SAME ORDER AS THE PREVIEW (sheets.jsx SupplierSheet): the
     meta line Visited / Products / Contacts, then the six tiles Decision,
     Rating, Best price, MOQ, Landed / unit, Final. The two layouts used to
     disagree — the PDF omitted the counts and the MOQ and added fields of its
     own — which is what made the download read as a different document. */
  const contacts = supplier.contacts || [];
  const moq = (products.find((p) => p.moq) || {}).moq;

  let cursor = keyValues(doc, y, [
    inFlow ? null : ['Fair', fair && fair.name],
    ['Visited', supplier.createdAt ? new Date(supplier.createdAt).toLocaleDateString() : null],
    ['Products', String(products.length)],
    ['Contacts', String(contacts.length)],
    ['Decision', supplierDecision(supplier)],
    ['Rating', supplierRating(supplier) ? `${supplierRating(supplier)}/5` : null],
    ['Best price', best != null ? money(best) : null],
    ['MOQ', moq || null],
    ['Landed / unit', l.perUnit != null ? money(l.perUnit) : null],
    ['Final', supplier.decision],
    ['Paid at booth', paidAtBooth(supplier) ? money(paidAtBooth(supplier)) : null],
    ['Contact', contact ? [contact.role, contact.phone, contact.wechat, contact.email].filter(Boolean).join(' · ') || null : null],
  ]);

  if (products.length) {
    cursor = heading(doc, cursor, 'Products');
    autoTable(doc, {
      startY: cursor,
      margin: { left: MARGIN, right: MARGIN },
      head: [['Product', 'Price', 'MOQ', 'Lead', 'Payment', 'Sample', 'Rating', 'Next step']],
      body: products.map((prod) => [
        prod.name || '',
        priceLine(prod, fair) || '',
        prod.moq || '',
        prod.productionTime || '',
        prod.paymentTerms || '',
        prod.sample || '',
        prod.rating ? `${prod.rating}/5` : '',
        prod.nextStep !== 'None' ? prod.nextStep : '',
      ]),
      styles: { font: 'helvetica', fontSize: 8, cellPadding: 2, textColor: INK },
      headStyles: { fillColor: NAVY, textColor: 255, fontStyle: 'bold', fontSize: 8 },
      alternateRowStyles: { fillColor: [247, 248, 250] },
      theme: 'grid',
    ...cjkCellHooks(doc),
      ...cjkCellHooks(doc),
    });
    cursor = doc.lastAutoTable.finalY + 6;
  }

  const flags = riskFlags(supplier);
  if (flags.length) {
    cursor = heading(doc, cursor, 'Risk flags');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const lines = doc.splitTextToSize(flags.join(' · '), 175);
    cursor = space(doc, cursor, lines.length * 4.4 + 4);
    doc.text(lines, MARGIN, cursor);
    cursor += lines.length * 4.4 + 4;
  }

  if (supplier.chinaNotes) {
    cursor = heading(doc, cursor, 'Fair notes');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const lines = doc.splitTextToSize(supplier.chinaNotes, 175);
    cursor = space(doc, cursor, Math.min(lines.length, 8) * 4.4 + 4);
    doc.text(lines, MARGIN, cursor);
    cursor += lines.length * 4.4 + 4;
  }

  const open = (supplier.followUps || []).filter((f) => f.status !== 'Done');
  if (open.length) {
    cursor = heading(doc, cursor, 'Open follow-ups');
    autoTable(doc, {
      startY: cursor,
      margin: { left: MARGIN, right: MARGIN },
      head: [['Type', 'Date', 'Status', 'Notes']],
      body: open.map((f) => [f.type, [dateLabel(f.date), f.time].filter(Boolean).join(' '), f.status, f.notes || '']),
      styles: { font: 'helvetica', fontSize: 8, cellPadding: 2, textColor: INK },
      headStyles: { fillColor: NAVY, textColor: 255, fontSize: 8 },
      theme: 'grid',
    ...cjkCellHooks(doc),
      ...cjkCellHooks(doc),
    });
    cursor = doc.lastAutoTable.finalY + 6;
  }

  return cursor;
}

/* ── Delivery ───────────────────────────────────────────────────────────── */

/**
 * Download means DOWNLOAD — the file lands in the Downloads folder, the same
 * as every other BeeNexa report (loanReport's downloadPdf is a plain
 * doc.save()).
 *
 * This used to try the Web Share sheet first and only fall back to saving,
 * which made the Download button indistinguishable from the Share button on
 * any browser that can share files: both opened the same sheet, and nothing
 * was ever saved. Sharing belongs to the Share button, which has its own
 * handler and its own fallback.
 */
async function deliver(doc, filename, asBlob) {
  const blob = doc.output('blob');
  if (asBlob) return blob;
  doc.save(filename);
  return blob;
}

/* ── The three reports ──────────────────────────────────────────────────── */

export async function exportSupplierPdf({ supplier, fair, filename, asBlob = false }) {
  // Pull the CJK face first when the report needs it, so the text can be
  // real glyphs rather than images. Resolves to null offline, and the
  // rasteriser covers that case.
  await loadCjkFont();
  const doc = newDoc();
  const y = header(
    doc,
    supplier.company || 'Supplier',
    (fair && fair.name) || '',
  );
  supplierBody(doc, y, supplier, fair);
  footers(doc);
  return deliver(doc, filename || `BeeNexa_Supplier_${safe(supplier.company || 'supplier')}.pdf`, asBlob);
}

export async function exportShortlistPdf({ suppliers, fair, filename, asBlob = false }) {
  // Pull the CJK face first when the report needs it, so the text can be
  // real glyphs rather than images. Resolves to null offline, and the
  // rasteriser covers that case.
  await loadCjkFont();
  const finalists = (suppliers || []).filter((s) => s.inShortlist);
  const doc = newDoc();
  let y = header(
    doc,
    'Final shortlist',
    [fair && fair.name, `${finalists.length} finalists`].filter(Boolean).join(' · '),
  );

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    head: [['Supplier', 'Product', 'Quoted', 'Landed/unit', 'MOQ', 'Risks', 'Decision']],
    body: finalists.map((s) => {
      const prod = (s.products || []).find((x) => x.decision === 'Strong') || (s.products || [])[0];
      const l = landed(s);
      return [
        s.company || '',
        (prod && prod.name) || '',
        prod ? priceLine(prod, fair) || '' : '',
        l.perUnit != null ? money(l.perUnit) : '',
        (prod && prod.moq) || '',
        String(riskFlags(s).length),
        s.decision || supplierDecision(s),
      ];
    }),
    styles: { font: 'helvetica', fontSize: 8, cellPadding: 2, textColor: INK },
    headStyles: { fillColor: NAVY, textColor: 255, fontSize: 8 },
    alternateRowStyles: { fillColor: [247, 248, 250] },
    theme: 'grid',
    ...cjkCellHooks(doc),
  });

  // Finalists FLOW, exactly as they do in the fair report. This used to call
  // addPage() per supplier and print a full page banner for each, so a
  // four-finalist shortlist was five sheets of mostly white — and it had
  // drifted from the fair report, which already flowed.
  y = doc.lastAutoTable.finalY + 6;

  const MIN_BLOCK_MM = 55;
  for (const s of finalists) {
    y = space(doc, y + 4, MIN_BLOCK_MM);
    y = supplierHeading(doc, y, s, fair);
    y = supplierBody(doc, y, s, fair, { inFlow: true });
  }

  footers(doc);
  return deliver(doc, filename || `BeeNexa_Final_Shortlist_${stamp()}.pdf`, asBlob);
}

export async function exportFairPdf({ suppliers, fair, filename, asBlob = false }) {
  // Pull the CJK face first when the report needs it, so the text can be
  // real glyphs rather than images. Resolves to null offline, and the
  // rasteriser covers that case.
  await loadCjkFont();
  const sorted = [...(suppliers || [])].sort(supplierSort);
  const doc = newDoc();
  let y = header(
    doc,
    (fair && fair.name) || 'Fair report',
    [fair && fair.location, fair && fair.dates, `${sorted.length} suppliers`].filter(Boolean).join(' · '),
  );

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    head: [['Supplier', 'Product', 'Price', 'MOQ', 'Rating', 'Next', 'Contact']],
    body: sorted.map((s) => {
      const prod = (s.products || [])[0];
      const contact = primaryContact(s);
      return [
        s.company || '',
        (prod && prod.name) || '',
        prod ? priceLine(prod, fair) || '' : '',
        (prod && prod.moq) || '',
        supplierRating(s) ? `${supplierRating(s)}/5` : '',
        prod && prod.nextStep !== 'None' ? prod.nextStep : '',
        contact ? contactLabel(contact, '') : '',
      ];
    }),
    styles: { font: 'helvetica', fontSize: 8, cellPadding: 2, textColor: INK },
    headStyles: { fillColor: NAVY, textColor: 255, fontSize: 8 },
    alternateRowStyles: { fillColor: [247, 248, 250] },
    theme: 'grid',
    ...cjkCellHooks(doc),
  });

  /**
   * Suppliers FLOW instead of taking a page each.
   *
   * A four-supplier fair used to print five sheets, most of them mostly empty,
   * because every supplier began with an unconditional addPage(). A buyer
   * printing a 60-supplier fair would have burned 61 sheets. Now a new page is
   * started only when there is not enough room left to begin the next block
   * usefully — `space()` already encapsulates that test.
   */
  // Continue BELOW the summary table. autoTable does not move `y` for us, and
  // when each supplier still began with addPage() that did not matter — the
  // page break hid it. Flowing them exposed it: the first supplier was drawn
  // straight over the summary rows.
  y = doc.lastAutoTable.finalY + 6;

  const MIN_BLOCK_MM = 55;
  for (const s of sorted) {
    y = space(doc, y + 4, MIN_BLOCK_MM);
    y = supplierHeading(doc, y, s, fair);
    y = supplierBody(doc, y, s, fair, { inFlow: true });
  }

  footers(doc);
  return deliver(doc, filename || `BeeNexa_Fair_Report_${safe((fair && fair.name) || 'fair')}_${stamp()}.pdf`, asBlob);
}

const safe = (s) => String(s).replace(/[^\w\-. ]+/g, '').trim().slice(0, 40) || 'report';
const stamp = () => new Date().toISOString().slice(0, 10);
